import React from 'react';
import * as Popover from '@radix-ui/react-popover';
import './MultiSelect.css';

function ChevronDownIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function MultiSelect({
  values = [],
  onChange,
  options = [],
  placeholder = 'All',
  maxLabelLength = 48,
  size = 'md',
  error = false,
  disabled = false,
  fullWidth = false,
  showClearAll = true,
  defaultOpen = false,
  'aria-label': ariaLabel,
  className = '',
}) {
  const selectedSet = new Set(values);
  const selectedOpts = options.filter((o) => selectedSet.has(o.value));

  let triggerLabel;
  if (selectedOpts.length === 0) triggerLabel = placeholder;
  else if (selectedOpts.length === 1) triggerLabel = selectedOpts[0].label;
  else {
    const joined = selectedOpts.map((o) => o.label).join(', ');
    triggerLabel = joined.length > maxLabelLength
      ? `${selectedOpts.length} selected`
      : joined;
  }

  const triggerTitle = selectedOpts.length > 1
    ? selectedOpts.map((o) => o.label).join(', ')
    : '';

  const toggle = (v) => {
    const next = new Set(selectedSet);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    onChange([...next]);
  };

  const triggerClasses = [
    'ui-multiselect',
    `ui-multiselect--${size}`,
    error ? 'ui-multiselect--error' : '',
    fullWidth ? 'ui-multiselect--full' : '',
    selectedOpts.length === 0 ? 'ui-multiselect--placeholder' : '',
    className,
  ].filter(Boolean).join(' ');

  return (
    <Popover.Root defaultOpen={defaultOpen}>
      <Popover.Trigger
        type="button"
        className={triggerClasses}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-invalid={error || undefined}
        title={triggerTitle || undefined}
      >
        <span className="ui-multiselect__label">{triggerLabel}</span>
        <span className="ui-multiselect__chevron">
          <ChevronDownIcon />
        </span>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          className="ui-multiselect__panel"
          sideOffset={4}
          align="start"
        >
          {showClearAll && selectedOpts.length > 0 && (
            <button
              type="button"
              className="ui-multiselect__clear-all"
              onClick={() => onChange([])}
            >
              Clear all
            </button>
          )}
          <div className="ui-multiselect__list" role="listbox" aria-multiselectable="true">
            {options.map((o) => {
              const checked = selectedSet.has(o.value);
              return (
                <button
                  key={o.value}
                  type="button"
                  role="option"
                  aria-selected={checked}
                  disabled={o.disabled}
                  className={`ui-multiselect__option ${checked ? 'ui-multiselect__option--checked' : ''}`}
                  onClick={() => !o.disabled && toggle(o.value)}
                >
                  <span className={`ui-multiselect__box ${checked ? 'ui-multiselect__box--checked' : ''}`}>
                    {checked && <CheckIcon />}
                  </span>
                  <span className="ui-multiselect__option-label">{o.label}</span>
                </button>
              );
            })}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

export default MultiSelect;
